import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

// Generic SEO exact-match landing page — driven by seoLandings.js data
const SeoLandingPage = ({ landing }) => {
  const { lang } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, [landing.slug]);
  const isFr = lang === "fr";
  const c = isFr ? landing.fr : landing.en;
  const base = "https://imoteps.doctor";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${base}/` },
      { "@type": "ListItem", position: 2, name: c.breadcrumb, item: `${base}/${landing.slug}` },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const medicalJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: c.h1,
    description: c.description,
    url: `${base}/${landing.slug}`,
    about: { "@type": "MedicalProcedure", name: c.breadcrumb, bodyLocation: landing.bodyLocation, procedureType: "Surgical" },
    mainEntity: {
      "@type": "Physician",
      name: "Dr Richard Aswad",
      medicalSpecialty: "Orthopedic",
      url: `${base}/`,
      telephone: SITE.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "118 Rue Jean Mermoz",
        postalCode: "13008",
        addressLocality: "Marseille",
        addressCountry: "FR",
      },
    },
  };

  return (
    <>
      <SEOHead
        title={c.title}
        description={c.description}
        canonical={`/${landing.slug}`}
        keywords={landing.keywords}
        lang={lang}
        type="article"
        jsonLd={medicalJsonLd}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <main data-testid={`seo-landing-${landing.slug}`} className="bg-white pt-28 md:pt-36 pb-20">
        <article className="container-page max-w-5xl">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-[#FF5A1F]">{isFr ? "Accueil" : "Home"}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF5A1F] font-semibold">{c.breadcrumb}</span>
          </nav>

          <header className="mt-6">
            <span className="chip">{c.chip}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              {c.h1}
            </h1>
            <p className="mt-5 text-lg text-foreground/80 leading-relaxed max-w-3xl">{c.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#contact" className="btn-primary" data-testid={`${landing.slug}-cta-contact`}>
                <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
                {isFr ? "Prendre rendez-vous" : "Book an appointment"}
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
            </div>
          </header>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">{c.whyTitle}</h2>
            <p className="mt-4 text-base text-foreground/80 leading-relaxed">{c.whyText}</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">8 000+</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Interventions réalisées" : "Surgeries performed"}</p>
              </div>
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">25+</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Années d'expérience" : "Years of experience"}</p>
              </div>
              <div className="rounded-xl border border-border p-5 bg-white">
                <p className="font-display text-3xl font-extrabold text-[#FF5A1F]">{isFr ? "Expert HAS" : "HAS Expert"}</p>
                <p className="mt-1 text-sm text-foreground/75">{isFr ? "Prothèses d'épaule" : "Shoulder prostheses"}</p>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">{c.proceduresTitle}</h2>
            <p className="mt-3 text-base text-foreground/75 max-w-3xl">{c.proceduresLead}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {c.procedures.map((p) => (
                <Link
                  key={p.slug}
                  to={`/pathologie/${p.slug}`}
                  data-testid={`${landing.slug}-procedure-${p.slug}`}
                  className="group rounded-xl border border-border bg-white p-6 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all"
                >
                  <p className="font-display font-bold text-navy text-lg group-hover:text-[#FF5A1F] transition-colors">{p.name}</p>
                  <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{p.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5A1F]">
                    {isFr ? "En savoir plus" : "Learn more"}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-14" data-testid={`${landing.slug}-faq`}>
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Questions fréquentes" : "Frequently asked questions"}
            </h2>
            <div className="mt-6 space-y-4">
              {c.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-white p-5 open:shadow-md transition-shadow">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                    <h3 className="font-display font-bold text-navy text-base leading-snug">{f.q}</h3>
                    <i className="fa-solid fa-chevron-down mt-1 text-[#FF5A1F] text-xs transition-transform group-open:rotate-180" aria-hidden="true"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#102A4C] text-white p-8 md:p-12">
            <h2 className="font-display text-3xl font-bold">
              {isFr ? "Consultation à la Clinique Juge — Marseille 13008" : "Consultation at Clinique Juge — Marseille 13008"}
            </h2>
            <p className="mt-4 text-white/85 max-w-3xl">
              {isFr
                ? "118, rue Jean Mermoz — 13008 Marseille. Tramway T3 (arrêt Périer), parking visiteurs sur place, accessibilité PMR. Réservation directe sur Doctolib ou par téléphone."
                : "118, rue Jean Mermoz — 13008 Marseille. Tram T3 (Périer stop), on-site visitor parking, reduced-mobility access. Direct booking on Doctolib or by phone."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={SITE.phoneHref} className="btn-primary">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
              <a href={SITE.doctolibUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost !text-white !border-white/30 hover:!bg-white/10">
                <i className="fa-solid fa-calendar-plus" aria-hidden="true"></i>Doctolib
              </a>
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default SeoLandingPage;
